import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import _ from "lodash"

import { Button, DropDownField, AutoCompleteField } from '../helpers';
import Loading from './Loading';

import { fetchCompanyTypes } from '../services/redux/reducers/commonThunk';
import { getCompanies } from '../services/api/company';
import { getBranchesList } from '../services/api/companyBranch';
import { getCompanyRolesData } from '../services/api/companyRole';
import { raiseToast } from '../services/redux/reducers/toast/toast';
import { getUserRolesByCompanyId, companyUserRoleValidation, searchUsers } from '../services/api/user';

import { AddUserRoleValidationSchema } from '../validation/addUserRoleValidation';
import { checkPermissions, validateInputs } from '../utils'


const AddUserRoles = (props) => {
    const { userId } = useParams()
    const dispatch = useDispatch()

    const [searchedCompanyNames, setSearchedCompanyNames] = useState([])
    const [userRole, setUserRole] = useState(
        {
            userRoleId: uuidv4(),
            userId: parseInt(userId),
            companyId: null,
            companyRoleId: null,
            branchId: null,
            managerId: null,
            statusId: 1,
            isNew: true
        }
    )
    const [companies, setCompanies] = useState([])
    const [companyBranches, setCompanyBranches] = useState([])
    const [companyRoles, setCompanyRoles] = useState([])
    const [companyUsers, setCompanyUsers] = useState([])
    const [companyRoleHierarchyUserIds, setCompanyRoleHierarchyUserIds] = useState([])
    const [formErrors, setFormErrors] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [userRoles, setUserRoles] = useState([])

    const companyTypes = useSelector(state => state.common.companyTypes)
    const companyRoleHierarchy = useSelector(state => state.common.companyRoleHierarchy)
    const userDetails = useSelector(state => state.userDetails)
    const PMCompanyDetails = useSelector(state => state.userDetails.PMCompanyDetails)

    useEffect(() => {
        dispatch(fetchCompanyTypes())
    },[])

    const getCompanyData = async (buyerSeller) => {
        const bodyData = { paginator: false, sortBy: 'companyName', sortOrder: "ASC", statusId: 1, buyerSeller: [buyerSeller, 3] }

        setIsLoading(true)
        const companiesResponse = await getCompanies(bodyData)
        setIsLoading(false)

        if (companiesResponse.failure) {
            return dispatch(raiseToast({ severity: 'error', detail: companiesResponse.data.error, life: 3000 }))
        } else {
            setCompanies(companiesResponse.data.data)
        }
    }

    const getCompanyBranchData = async (companyId) => {
        const data = { paginator: false, companyIds: companyId, sortBy: 'branchName', sortOrder: "ASC", statusId: 1 }
        const companyBranchesResponse = await getBranchesList(data)

        if (companyBranchesResponse.failure) {
            return dispatch(raiseToast({ severity: 'error', detail: companyBranchesResponse.data.error, life: 3000 }))
        } else {
            const isPMCompany = companyId[0] === PMCompanyDetails.companyId

            let companyBranches = companyBranchesResponse.data.data
            let relatedCompanyBranches = companyBranches.filter(eachBranch => eachBranch.buyerSeller === userRole.buyerSeller)
            if ([2, 3].includes(userRole?.buyerSeller) && !isPMCompany) {
                setCompanyBranches([...relatedCompanyBranches])
            } else {
                setCompanyBranches([{ branchId: 0, branchName: 'All' }, ...relatedCompanyBranches])
            }
        }
    }

    const getCompanyRoles = async (companyId) => {
        const data = { paginator: false, companyIds: companyId, sortBy: 'companyRoleName', sortOrder: "ASC", statusId: 1 }
        const companyRoleData = await getCompanyRolesData(data)
        
        if (companyRoleData.failure) {
            return dispatch(raiseToast({ severity: 'error', detail: companyRoleData.data.error, life: 3000 }))
        } else{
            setCompanyRoles(companyRoleData.data.data)
        }
    }

    const getCompanyUsers = async (companyIds) => {
        const data = { paginator: false, companyIds: companyIds, sortBy: 'firstName', sortOrder: "ASC", statusId: 1 }
        const companyUsersData = await searchUsers(data)

        if (companyUsersData.failure) {
            return dispatch(raiseToast({ severity: 'error', detail: companyUsersData.data.error, life: 3000 }))
        } else{
            let updatedManagerUsers = companyUsersData.data.data.map((eachUser) => {
                if(eachUser?.firstName && eachUser?.lastName){
                    return {
                        ...eachUser,
                        firstName: `${eachUser.firstName} ${eachUser.lastName}`
                    }
                }else{
                    return {
                        ...eachUser
                    }
                }
            })

            setCompanyUsers(updatedManagerUsers)
        }
    }

    const getUserRolesData = async (companyId) => {
        const data = { companyId }
        const response = await getUserRolesByCompanyId(data)
        
        if (response.failure) {
            return dispatch(raiseToast({ severity: 'error', detail: response.data.error, life: 3000 }))
        } else{
            let userRolesData = response?.data?.filter(eachUser => eachUser.statusId === 1)
            setUserRoles(userRolesData)
        }
    }

    const handleSubmitUserRole = async () => {
        let selectedcompanyId = userRole.companyId ? userRole.companyId.companyId : null
        let updatedCompanyDetails = { ...userRole, companyId: selectedcompanyId}

        const validation_res = await validateInputs(AddUserRoleValidationSchema, updatedCompanyDetails )

        if (validation_res) {
            if (validation_res.error) {
                setFormErrors(validation_res.error)
            }
            else {
                setFormErrors({})
                let selectedCompany = companies.find(company => company.companyId === validation_res.result.companyId)
                let selectedBranch = companyBranches.find(branch => branch.branchId === validation_res.result.branchId)
                let selectedCompanyRole = companyRoles.find(roles => roles.companyRoleId === validation_res.result.companyRoleId)
                let relatedManager = companyUsers.find(users => users.userId === validation_res.result.managerId)
                
                let companyRoleHierarchyCompanyIds = _.uniqBy(companyRoleHierarchy, 'companyId').map(obj => obj.companyId)
                if (companyRoleHierarchyCompanyIds.includes(selectedCompany.companyId)) {
                    if (selectedBranch) {
                        if ([3, 13].includes(selectedCompanyRole.companyRoleId)) {
                            let data = {
                                companyRoleName: selectedCompanyRole.companyRoleName,
                                companyRoleId: selectedCompanyRole.companyRoleId,
                                companyId: selectedCompany.companyId,
                                branchId: selectedBranch.branchId,
                                pmCompanyId: PMCompanyDetails.pmCompanyId
                            }
    
                            setIsLoading(true)
                            const companyUserRoleValidationResponse = await companyUserRoleValidation(data)
                            setIsLoading(false)
                            console.log("companyUserRoleValidationResponse", companyUserRoleValidationResponse)
                            if (companyUserRoleValidationResponse.failure) {
                                return dispatch(raiseToast({ severity: 'error', detail: companyUserRoleValidationResponse.data.error, life: 3000 }))
                            } else {
                                let responseData = companyUserRoleValidationResponse.data
                                if(responseData){
                                    if(selectedCompanyRole.companyRoleId === 3){
                                        return dispatch(raiseToast({ severity: 'error', detail: "The company branch can only have one branch head", life: 3000 }))
                                    }
                        
                                    if(selectedCompanyRole.companyRoleId === 13){
                                        return dispatch(raiseToast({ severity: 'error', detail: "The company branch can only have one regional head", life: 3000 }))
                                    }
                                }
                            }
                        }
                    }
                }
                
                props.handleAddUserRole(validation_res.result, selectedCompany, selectedBranch, selectedCompanyRole, relatedManager)
            }
        }
    }

    const handleInputChange = (event) => {
        userRole[event.target.name] = event.target.value
        
        if( event.target.name === 'companyId'){
            if (event.target.value && typeof (event.target.value) === "object") {
                getCompanyBranchData([event.target.value.companyId])
                getCompanyRoles([event.target.value.companyId])
                getCompanyUsers([event.target.value.companyId])

                if(event.target.value.companyId === 1){
                    getUserRolesData(event.target.value.companyId)
                }
            }else{
                const relatedCompany = companies.find(company => company.companyName.toLowerCase() === event.target.value?.trim().toLowerCase())
                if(relatedCompany && relatedCompany?.companyId){
                    getCompanyBranchData([relatedCompany.companyId])
                    getCompanyRoles([relatedCompany.companyId])
                    getCompanyUsers([relatedCompany.companyId])
                }else{
                    setCompanyBranches([])
                    setCompanyRoles([])
                    setCompanyUsers([])
                    setCompanyRoleHierarchyUserIds([])
                }
            }
        }
        else if (event.target.name === "companyRoleId") {
            if (userRole?.companyId?.companyId === PMCompanyDetails.companyId) {
                let {isPermitted} = checkPermissions({ ...userDetails, userRoleId: userDetails.activeRoleId, permissions: [147] })
                if (isPermitted) {
                    userRole[event.target.name] = event.target.value
                } else if ([2, 11, 48425, 61270, 61269, 61268].includes(event.target.value)) {
                    userRole[event.target.name] = event.target.value
                } else {
                    userRole[event.target.name] = null
                    setUserRole({ ...userRole })
                    return dispatch(raiseToast({ severity: 'error', detail: `Selected User Role Can't be Assigned.`, life: 3000 }))
                }

                let relatedCompanyRoleHierarchy = companyRoleHierarchy.filter(eachCompanyRole => eachCompanyRole.companyRoleId === event.target.value)
                if (relatedCompanyRoleHierarchy.length > 0) {
                    const parentRoleIds = _.uniqBy(relatedCompanyRoleHierarchy, 'parentRoleId').map(obj => obj.parentRoleId)
                    const companyRoleHierarchyUserIds = userRoles
                                                            .filter(eachUserRole => parentRoleIds.includes(eachUserRole.companyRoleId))
                                                            .map(eachUserRole => eachUserRole.userId)
                    setCompanyRoleHierarchyUserIds(companyRoleHierarchyUserIds)
                } else {
                    setCompanyRoleHierarchyUserIds([])
                }
            }

            if(userRole?.managerId){
                userRole.managerId = null
            }
        }
        else if (event.target.name === "managerId"){
            if(!userRole?.companyRoleId){
                return dispatch(raiseToast({ severity: 'error', detail: `Please assign company role.`, life: 3000 }))
            }
        } 
        else if (event.target.name === 'buyerSeller') {
            userRole.companyId = null
            userRole.companyRoleId = null
            userRole.branchId = null
            userRole.managerId = null

            getCompanyData(event.target.value)
            setCompanies([])
            setCompanyBranches([])
            setCompanyRoles([])
            setCompanyUsers([])
            setCompanyRoleHierarchyUserIds([])
        }
        else{
            
        }
        setUserRole({ ...userRole })
    }

    const searchCompany = (event) => {
        let uniqueCompanies = []
        const relatedCompanies = companies.filter(company => {
            if(company.companyName?.toLowerCase().includes(event.query.trim()?.toLowerCase())){
                if(!uniqueCompanies.includes(company.companyName?.toLowerCase())){
                    uniqueCompanies.push(company.companyName?.toLowerCase())
                    return true
                }
            }
        })
        setSearchedCompanyNames([...relatedCompanies])
    }

    let branchManagerIds = []
    if (userRole.companyId?.companyId === 1) {
        let relatedCompanyRoleHierarchy = companyRoleHierarchy.filter(eachCompanyRole => eachCompanyRole.companyRoleId === userRole.companyRoleId)
        if (relatedCompanyRoleHierarchy.length > 0) {
            const parentRoleIds = _.uniqBy(relatedCompanyRoleHierarchy, 'parentRoleId').map(obj => obj.parentRoleId)
            let isCompanyLevelRole = [1, 8, 10].includes(parentRoleIds[0])
            let companyRoleHierarchyUserIds = []
            if(isCompanyLevelRole){
                companyRoleHierarchyUserIds = userRoles
                .filter(eachUserRole => parentRoleIds.includes(eachUserRole.companyRoleId))
                .map(eachUserRole => eachUserRole.userId)
            } else {
                companyRoleHierarchyUserIds = userRoles
                .filter(eachUserRole => parentRoleIds.includes(eachUserRole.companyRoleId) && eachUserRole.branchId === userRole.branchId)
                .map(eachUserRole => eachUserRole.userId)
            }
            
            if (companyRoleHierarchyUserIds.length > 0) {
                branchManagerIds = userRoles.filter(eachUser => companyRoleHierarchyUserIds.includes(eachUser.userId)).map(eachUserRole => eachUserRole.userId)
            }
        }
    } else {
        branchManagerIds = userRoles.map(eachUserRole => eachUserRole.userId)
    }

    const relatedBranchManagers = companyUsers.filter(eachUser => branchManagerIds.includes(eachUser.userId))
    const relatedCompanyTypes = companyTypes.filter(eachCompanyType => eachCompanyType.companyTypeId != 3)
    return (
        <div className=''>
            <div className='grid'>
                <div className="flex flex-column col-12 md:col-6">
                    <DropDownField name="buyerSeller" labelText="Branch Type" placeholder={"Please Select"} options={relatedCompanyTypes} optionLabel="companyType" optionValue="companyTypeId" value={userRole.buyerSeller} showClear onChange={(event) => handleInputChange(event)} errorMessage={formErrors.buyerSeller} />
                </div>
                <div className="flex flex-column col-12 md:col-6">
                    <AutoCompleteField name="companyId" labelText="Company" field="companyName" placeholder="Search company" value={userRole.companyId} suggestions={searchedCompanyNames} searchMethod={searchCompany} onChange={(event) => handleInputChange(event)} dropdown forceSelection errorMessage={formErrors.companyId} />
                </div>
                <div className="flex flex-column col-12 md:col-6">
                    <DropDownField name="branchId" labelText="Branch" optionLabel="branchName" optionValue="branchId" options={companyBranches} value={userRole.branchId} onChange={(event) => handleInputChange(event)} errorMessage={formErrors.branchId} />
                </div>
                <div className="flex flex-column col-12  md:col-6">
                    <DropDownField name="companyRoleId" labelText="Role" optionLabel="companyRoleName" optionValue="companyRoleId" options={companyRoles} value={userRole.companyRoleId} onChange={(event) => handleInputChange(event)} errorMessage={formErrors.companyRoleId} />
                </div>
                <div className="flex flex-column col-12 md:col-6">
                    <DropDownField name="managerId" labelText="Manger" optionLabel="firstName" optionValue="userId" options={relatedBranchManagers} value={userRole.managerId} onChange={(event) => handleInputChange(event)} errorMessage={formErrors.managerId} /> 
                    {/* <DropDownField name="managerId" labelText="Manger" options={relatedBranchManagers} optionValue="userId" optionLabel={(eachRole) => eachRole.user.lastName ? `${eachRole.user.firstName} ${eachRole.user.lastName}` : `${eachRole.user.firstName}`} value={userRole.managerId} onChange={(event) => handleInputChange(event)} errorMessage={formErrors.managerId} />  */}
                </div>
            </div>
            <div className='mt-3 flex justify-content-end'>
                <Button label="Add Role" onClick={(event) => handleSubmitUserRole(event)} />
            </div>
            <Loading visible={isLoading} />
        </div>
    )
};

export default AddUserRoles;